import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import styles from './BotaoDropdown.module.css';
import Icon from '@mdi/react';
import { mdiChevronDown, mdiChevronUp } from '@mdi/js';
import { BotaoVariants } from './Botao';
import btnStyles from './Botao.module.css';
import HBox from './layout/HBox';
import ReactDOM from 'react-dom';

type DropdownPosition =
  | 'top-right'
  | 'top-left'
  | 'bottom-right'
  | 'bottom-left';

type BotaoDropdownProps = {
  variant?: BotaoVariants;
  size?: 'sm' | 'md' | 'lg';
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
  options?: { id: string; label: React.ReactNode }[];
  disabled?: boolean;
  children?: React.ReactNode;
  text?: React.ReactNode;
  icon?: string;
  noArrow?: boolean;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  position?: DropdownPosition | 'auto';
  [key: string]: any;
};

export default function BotaoDropdown({
  variant = 'primary',
  size = 'md',
  value,
  onChange,
  placeholder,
  type = 'text',
  required,
  options,
  disabled,
  children,
  text,
  icon,
  noArrow,
  onKeyDown,
  position = 'auto',
  ...props
}: Readonly<BotaoDropdownProps>) {
  const [open, setOpen] = React.useState<{
    isOpen: boolean;
    position: DropdownPosition;
  }>({ isOpen: false, position: 'bottom-right' });

  const handleContainerClick = (e: any) => {
    e.stopPropagation();

    if (disabled) return;

    toggleDropdown();
  };

  const toggleDropdown = useCallback(() => {
    console.log('toggle', open.isOpen);
    setOpen(prevState => ({ ...prevState, isOpen: !prevState.isOpen }));
  }, [open]);

  const openDropdown = () => {
    setOpen(prevState => ({ ...prevState, isOpen: true }));
  };

  const closeDropdown = () => {
    setOpen(prevState => ({ ...prevState, isOpen: false }));
  };

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        e.target instanceof HTMLElement &&
        e.target.closest(`.${styles.container}`)
      ) {
        return;
      }
      closeDropdown();
    };
    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, []);

  const optionsContainerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [positioned, setPositioned] = React.useState(false);

  const computeOffset = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setOffset({
      top: rect.bottom + window.scrollY,
      bottom: window.innerHeight - rect.top + window.scrollY,
      left: rect.left + window.scrollX,
      right: window.innerWidth - rect.right - window.scrollX,
    });
  }, []);

  const computePosition = useCallback(() => {
    if (!optionsContainerRef.current || !containerRef.current) return;
    if (position === 'auto') {
      const optRect = optionsContainerRef.current.getBoundingClientRect();
      const buttonRect = containerRef.current.getBoundingClientRect();
      const availableSpaceBelow = window.innerHeight - buttonRect.bottom;
      const availableSpaceAbove = buttonRect.top;
      const shouldOpenAbove =
        availableSpaceBelow < optRect.height &&
        availableSpaceAbove > availableSpaceBelow;

      const availableSpaceRight = window.innerWidth - buttonRect.right;
      const availableSpaceLeft = buttonRect.left;
      const shouldOpenLeft =
        availableSpaceRight < optRect.width &&
        availableSpaceLeft > availableSpaceRight;

      const _position = shouldOpenAbove
        ? shouldOpenLeft
          ? 'top-left'
          : 'top-right'
        : shouldOpenLeft
          ? 'bottom-left'
          : 'bottom-right';

      setOpen(prevState => ({ ...prevState, position: _position }));
    } else {
      setOpen(prevState => ({
        ...prevState,
        position: position as DropdownPosition,
      }));
    }
  }, [position]);

  const [offset, setOffset] = React.useState({
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  });

  // Compute offset + position synchronously before browser paints
  useLayoutEffect(() => {
    if (open.isOpen) {
      computeOffset();
      computePosition();
      setPositioned(true);
    } else {
      setPositioned(false);
    }
  }, [open.isOpen, position, computeOffset, computePosition]);

  // Keep offset in sync on scroll/resize
  useEffect(() => {
    if (!open.isOpen) return;
    const adjust = () => computeOffset();
    window.addEventListener('scroll', adjust);
    window.addEventListener('resize', adjust);

    return () => {
      window.removeEventListener('scroll', adjust);
      window.removeEventListener('resize', adjust);
    };
  }, [open.isOpen, computeOffset]);

  return (
    <div>
      <div
        ref={containerRef}
        className={[
          btnStyles.button,
          styles.container,
          btnStyles[variant],
          btnStyles[size],
          disabled ? styles.disabled : null,
        ].join(' ')}
        tabIndex={disabled ? undefined : 0}
        onKeyDown={onKeyDown}
        onClick={handleContainerClick}
      >
        <HBox>
          {icon && <Icon path={icon} className={btnStyles.icon} />}
          {!!text && <div>{text}</div>}
          {!noArrow && (
            <div className={styles.arrow}>
              <Icon
                path={open.isOpen ? mdiChevronUp : mdiChevronDown}
                size={1}
              />
            </div>
          )}
        </HBox>

        {open.isOpen &&
          ReactDOM.createPortal(
            <div
              ref={optionsContainerRef}
              className={styles.options}
              style={{
                visibility: positioned ? 'visible' : 'hidden',
                top: open.position === 'bottom-left' || open.position === 'bottom-right' ? offset.top : undefined,
                bottom: open.position === 'top-left' || open.position === 'top-right' ? offset.bottom : undefined,
                left: open.position === 'bottom-right' || open.position === 'top-right' ? offset.left : undefined,
                right: open.position === 'bottom-left' || open.position === 'top-left' ? offset.right : undefined,
              }}
            >
              {/* <CampoSeletorOptions
                value={options}
                onSelect={v => {
                  onChange?.(v);
                  setOpen(false);
                }}
                noSearch={noSearch}
                search={search}
                onChangeSearch={setSearch}
                selectedId={selectedId}
                onChangeSelectedId={setSelectedId}
              /> */}
              {children}
            </div>,
            document.querySelector('#root')!,
          )}
      </div>
    </div>
  );
}
